import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { Class } from '@/types/classTypes';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';

interface AttendanceTrackerProps {
  organizationId: string;
  classId: string | null;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

interface Attendance {
  id?: string;
  student_id: string;
  class_id: string;
  date: string;
  status: 'present' | 'absent' | 'excused' | 'late';
  notes?: string;
  organization_id: string;
}

const AttendanceTracker: React.FC<AttendanceTrackerProps> = ({
  organizationId,
  classId: initialClassId,
}) => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(initialClassId);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [students, setStudents] = useState<Student[]>([]);
  const [attendance, setAttendance] = useState<Record<string, Attendance>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Fetch classes for the organization
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const { data, error } = await supabase
          .from('classes')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .order('name');
          
        if (error) throw error;
        setClasses(data || []);
        
        // If no class is selected and we have classes, select the first one
        if (!selectedClassId && data && data.length > 0) {
          setSelectedClassId(data[0].id);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };
    
    fetchClasses();
  }, [organizationId, selectedClassId]);

  // Fetch enrolled students for the selected class
  useEffect(() => {
    const fetchStudents = async () => {
      if (!selectedClassId) return;
      
      setLoading(true);
      try {
        // Instead of using RPC, get enrollments directly with student data
        const { data, error } = await supabase
          .from('enrollments')
          .select(`
            student_id,
            students (
              id,
              first_name,
              last_name
            )
          `)
          .eq('class_id', selectedClassId)
          .eq('status', 'active');
          
        if (error) throw error;
        
        // Extract student data from the nested query
        const formattedStudents = data?.map(enrollment => ({
          id: enrollment.student_id,
          first_name: enrollment.students.first_name,
          last_name: enrollment.students.last_name
        })) || [];
        
        setStudents(formattedStudents);
      } catch (error) {
        console.error('Error fetching enrolled students:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudents();
  }, [selectedClassId]);

  // Fetch existing attendance records
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!selectedClassId || !selectedDate) return;
      
      setLoading(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        
        const { data, error } = await supabase
          .from('class_attendance')
          .select('*')
          .eq('class_id', selectedClassId)
          .eq('date', dateStr);
          
        if (error) throw error;
        
        // Convert array to record by student_id for easier access
        const attendanceMap: Record<string, Attendance> = {};
        data?.forEach(record => {
          // Ensure status is one of our valid types
          const validStatus = ['present', 'absent', 'excused', 'late'].includes(record.status) 
            ? record.status as 'present' | 'absent' | 'excused' | 'late'
            : 'absent';
            
          attendanceMap[record.student_id] = {
            ...record,
            status: validStatus
          };
          
          if (record.notes) {
            setNotes(prev => ({ ...prev, [record.student_id]: record.notes || '' }));
          }
        });
        
        setAttendance(attendanceMap);
      } catch (error) {
        console.error('Error fetching attendance:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAttendance();
  }, [selectedClassId, selectedDate]);

  const handleAttendanceChange = (studentId: string, status: 'present' | 'absent' | 'excused' | 'late') => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {
          student_id: studentId,
          class_id: selectedClassId!,
          date: format(selectedDate, 'yyyy-MM-dd'),
          organization_id: organizationId,
        }),
        status,
      } as Attendance,
    }));
  };

  const handleNotesChange = (studentId: string, value: string) => {
    setNotes(prev => ({
      ...prev,
      [studentId]: value,
    }));
  };

  const saveAttendance = async () => {
    if (!selectedClassId) return;
    
    setSaving(true);
    try {
      const attendanceRecords = Object.values(attendance).map(record => ({
        ...record,
        notes: notes[record.student_id] || '',
      }));
      
      // Upsert attendance records
      const { error } = await supabase
        .from('class_attendance')
        .upsert(attendanceRecords, { onConflict: 'student_id,class_id,date' });
        
      if (error) throw error;
      
      toast.success('Attendance saved successfully');
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const getSelectedClassName = () => {
    const selectedClass = classes.find(c => c.id === selectedClassId);
    return selectedClass ? selectedClass.name : 'Select a class';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-semibold">Attendance Tracker</CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Class</label>
              <Select
                value={selectedClassId || ''}
                onValueChange={(value) => setSelectedClassId(value)}
                disabled={classes.length === 0}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((cls) => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Date</label>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                disabled={!selectedClassId}
              />
            </div>
          </div>

          {!selectedClassId ? (
            <div className="flex flex-col items-center justify-center text-center py-10 px-6">
              <div className="rounded-full bg-muted/30 p-3 mb-4">
                <Calendar className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-1">No Class Selected</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Please select a class to track attendance
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-10 px-6">
              <div className="rounded-full bg-muted/30 p-3 mb-4">
                <Calendar className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-1">No Students Enrolled</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                There are no students enrolled in {getSelectedClassName()}
              </p>
            </div>
          ) : (
            <>
              <Table className="border rounded-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => {
                    const currentStatus = attendance[student.id]?.status || 'absent';
                    return (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">
                          {student.first_name} {student.last_name}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={currentStatus}
                            onValueChange={(value) => 
                              handleAttendanceChange(
                                student.id, 
                                value as 'present' | 'absent' | 'excused' | 'late'
                              )
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="present">Present</SelectItem>
                              <SelectItem value="absent">Absent</SelectItem>
                              <SelectItem value="excused">Excused</SelectItem>
                              <SelectItem value="late">Late</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="Add notes..."
                            value={notes[student.id] || ''}
                            onChange={(e) => handleNotesChange(student.id, e.target.value)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={saveAttendance}
                  disabled={saving || students.length === 0}
                >
                  {saving ? 'Saving...' : 'Save Attendance'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendanceTracker; 